// Item / lot form schemas — shared between the admin-web ItemForm (React Hook
// Form via zodResolver) and the two create routes (POST /api/inventory,
// POST /api/auctions/[id]/lots) via parseBody. Lives in @auctogon/core-admin so
// the client form and the server routes read the EXACT same validation rules,
// eliminating the client/server drift ADR 016 exists to close (BL-830 — the
// specific ask from Michael/Dan: required-ness validated identically on both
// sides).
//
// BL-1433 made the seller rule SYMMETRIC and unconditional. `addSellerRequiredIssue`
// is the single source of truth for "every lot carries a consignor," and all
// three schemas below apply it — the client form AND both server payload
// schemas. Two things changed from its predecessor
// (`addSellerRequiredUnlessLandIssue`), and both matter:
//
//   1. The Land carve-out is gone. Land was the one category allowed to have no
//      on-platform seller, which made the land flow the last door that could
//      still mint a sellerless lot. `auction_lots.seller_id` is NOT NULL now, so
//      a category-keyed exemption would only produce rows the column refuses.
//   2. The servers apply it. It used to have exactly ONE call site — the client
//      form — so the API accepted payloads the form rejected. That asymmetry is
//      what BUG-1357 surfaced from the operator's side: an already-sellerless
//      lot could not be edited at all, through either door, because the form
//      refused a payload its own API would have taken with a 200.
//
// Do not reintroduce a client-only rule here. If a rule is worth enforcing it
// belongs on both sides; if the servers cannot enforce it because a legitimate
// non-form caller would break, that is evidence the rule is wrong, not evidence
// it should live on one side.

import { z } from '@auctogon/core/zod';
import { isLandCategory } from '../isomorphic/item-form';
import { normalizeSellerId } from '../isomorphic/customers/normalize-seller-id';
import { addAdditionalContactPhoneIssue, addOtherContactReachableIssue, LotContactSlotFields, type LotContactRuleOptions } from './lot-contacts';
// BL-889: the create route's field messages are the SAME strings the shared
// inventory field schemas use. Imported from the leaf module (not `./inventory`,
// which imports this file) and deliberately NOT re-exported — the `./schemas`
// barrel `export *`s both files, so re-exporting here would make the names
// ambiguous. `./inventory` is the canonical public path for them.
import {
  StagedDocumentRefArraySchema,
  StagedMediaRefArraySchema,
  type StagedDocumentRef,
  type StagedMediaRef,
} from './staged-upload';
import { InternalNoteBodyArraySchema } from './internal-note';
import { OfflineOpIdSchema } from './offline-op';
// BL-2131: the ONE lot-money field — validate-only for the form's zodResolver,
// validate + decode for the create routes (ADR 016 — Form Validation).
import { lotMoneyWireField, lotMoneyWireSchema } from './lot-monetary-fields';
import type { MoneyWire } from '@auctogon/core/money';
import type { CustomerIdentityDto } from './responses/customer-identity';
import {
  INVENTORY_VIN_MESSAGE,
  INVENTORY_YEAR_MESSAGE,
  INVENTORY_HOURS_MILEAGE_MESSAGE,
} from './inventory-messages';

/**
 * Shared rule: every item/lot carries a seller (its consignor). Adds a Zod issue
 * on the seller path so `parseBody` → `fieldErrors.sellerId` and RHF both render
 * the error inline under the Seller field rather than in the root banner.
 *
 * Applied by all three schemas in this file — the client form and both server
 * payload schemas — so the form rejects exactly what the routes reject.
 *
 * Emptiness is decided by the shared {@link normalizeSellerId}, the same seam
 * the routes use, so `''` and whitespace-only are "absent" here and at the write
 * path. That matters: the forms send `''` for "no seller" (ItemForm seeds
 * `sellerId: ''` and re-sets it when the seller search is cleared), so a
 * presence-only check would let the real-world payload straight through to a
 * uuid column — the BUG-1003 22P02.
 *
 * BL-1433 removed the `isLandCategory` exemption this rule used to carry; see
 * the file header for why.
 *
 * @param data  the parsed object; only `sellerId` is read
 * @param ctx   the Zod refinement context to add the issue to
 * @param opts.sellerPath  field path the issue lands on (default `sellerId`)
 */
export function addSellerRequiredIssue(
  data: { sellerId?: string | null },
  ctx: z.RefinementCtx,
  opts?: { sellerPath?: string },
): void {
  if (normalizeSellerId(data.sellerId) !== null) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: SELLER_REQUIRED_MESSAGE,
    path: [opts?.sellerPath ?? 'sellerId'],
  });
}

/**
 * User-facing copy for a land per-acre lot missing its acreage. Keyed to the
 * `acreage` API field. Preserved verbatim from the hand-rolled server check
 * BL-1064 replaced — it's the string operators already know, and the lot-create
 * payload builders reference it by name in their comments.
 */
/**
 * User-facing copy for an item/lot with no seller. Exported so every surface
 * asserts against the constant rather than a literal — including the catalog
 * app, whose hand-rolled twin of this rule BL-1433 collapsed into this one.
 */
export const SELLER_REQUIRED_MESSAGE = 'Seller is required';

export const ACREAGE_REQUIRED_MESSAGE =
  'Acreage is required and must be positive for per-acre pricing';

/**
 * Shared conditional rule (BL-1064): a **Land** lot priced **per acre** must
 * carry a positive acreage. Adds a Zod issue on the `acreage` path so
 * `parseBody` → `fieldErrors.acreage` and RHF both render the error inline
 * under the Acreage field, rather than in the top root banner.
 *
 * Replaces a hand-rolled `if` in `POST /api/auctions/[id]/lots` that returned a
 * bare top-level `error` string with no `fieldErrors` (ADR 016 violation on two
 * counts: rule not in the shared schema, error not field-keyed).
 *
 * Like {@link addSellerRequiredIssue}, this rule is SYMMETRIC — applied by both
 * `ItemFormSchema` (client) and `LotCreatePayloadSchema` (server). Acreage is a
 * genuine data-integrity invariant for per-acre billing math: a per-acre price
 * with no acreage has no total to bill. Symmetry is the norm in this file, not
 * the exception — BL-1433 removed the last client-only rule.
 *
 * Two deliberate properties of the predicate:
 *
 * 1. **Keyed on the Land category, not on `pricingType` alone.** `ItemForm`
 *    seeds `pricingType: 'per_acre'` and only resets it once a NON-land category
 *    is picked, so a no-category item still carries the stale default. Firing on
 *    `pricingType` alone would re-break BUG-1085 for every categoryless save.
 *    (This is a slight loosening of the old server check, which had no category
 *    conjunct — inert in practice: `formDataToCreateLotBody` nulls both land
 *    fields for non-land, and the per-acre pricing math only engages for Land.)
 * 2. **`Number(...)` coercion rather than a `typeof === 'number'` narrow**, so a
 *    numeric-string acreage keeps passing exactly as it did under the old
 *    truthiness check. `null` → 0, `undefined`/`''`/`'abc'` → NaN; all fail.
 *
 * @param data  the parsed object; only `categoryId` / `pricingType` / `acreage`
 *              are read. Typed as `unknown` so both the strictly-typed client
 *              form shape and the permissive server payload shape satisfy it.
 * @param ctx   the Zod refinement context to add the issue to
 */
export function addAcreageRequiredForPerAcreIssue(
  data: { categoryId?: unknown; pricingType?: unknown; acreage?: unknown },
  ctx: z.RefinementCtx,
): void {
  const error = checkAcreageRequiredForPerAcre(data);
  if (!error) return;

  ctx.addIssue({ code: z.ZodIssueCode.custom, message: error, path: ['acreage'] });
}

/**
 * THE predicate behind {@link addAcreageRequiredForPerAcreIssue} — the same rule,
 * callable outside Zod (BL-1271).
 *
 * Extracted because the lot UPDATE doors need this verdict at a point a parse gate
 * cannot reach. `LotCatalogPatchSchema` is a PARTIAL-update schema, so the shape
 * that actually blanks a tract's acreage — a bare `{ "acreage": null }` from a
 * script or a stale client — carries no `categoryId` / `pricingType` for a refine
 * to read. The doors resolve that context against the STORED lot row and then ask
 * this function, which is why it takes plain data and returns a message rather
 * than writing into a `RefinementCtx`. See
 * `packages/admin-isomorphic/src/isomorphic/lots/validate-acreage-for-per-acre.ts`.
 *
 * The refine above is now a wrapper over this, so the client form, both create
 * schemas, and both update doors cannot drift apart — there is one sentence, in
 * one place (BL-1271 AC 2).
 *
 * @param data - only `categoryId` / `pricingType` / `acreage` are read; typed as
 *   `unknown` so the strict client form shape and the permissive server payload
 *   shape both satisfy it.
 * @returns {@link ACREAGE_REQUIRED_MESSAGE} when the rule is violated, else `null`.
 */
export function checkAcreageRequiredForPerAcre(data: {
  categoryId?: unknown;
  pricingType?: unknown;
  acreage?: unknown;
}): string | null {
  const categoryId = typeof data.categoryId === 'string' ? data.categoryId : null;
  if (!isLandCategory(categoryId) || data.pricingType !== 'per_acre') return null;

  const acreage = Number(data.acreage);
  if (Number.isFinite(acreage) && acreage > 0) return null;

  return ACREAGE_REQUIRED_MESSAGE;
}

/** User-facing copy for a blank item title. Keyed to the `title` API field. */
export const TITLE_REQUIRED_MESSAGE = 'Title is required';

/** User-facing copy for a blank lot listing name. Keyed to the `name` API field. */
export const LOT_NAME_REQUIRED_MESSAGE = 'Name is required';

/**
 * THE single definition of "an item's title may not be blank" (BUG-975).
 *
 * Consumed by `ItemFormSchema` (client form), `InventoryCreatePayloadSchema`
 * (POST), and — via `safeParse` on the bare field — the item/lot edit panel's
 * client-side gate in `useItemDetails` (and the lot-catalog PATCH route).
 * One definition is what makes "the create form and the edit panel enforce the
 * same rule" true by construction rather than by convention: BUG-975 existed
 * precisely because the create path had a rule and the edit path had none.
 *
 * `{ error }` covers a MISSING value (undefined); `.trim().min(1)` covers
 * present-but-blank, including whitespace-only ('   ' is truthy, so a bare
 * `if (!title)` guard would let it through).
 */
export const RequiredTitleSchema = z
  .string({ error: TITLE_REQUIRED_MESSAGE })
  .trim()
  .min(1, TITLE_REQUIRED_MESSAGE);

/**
 * The lot-listing-name twin of `RequiredTitleSchema`. Separate because the API
 * field is `name` (→ `auction_lots.name_for_auction`) and its message names
 * that field. BL-765 keeps a lot's listing name independent of its inventory
 * item's title — either may differ from the other, but neither may be blank.
 */
export const RequiredLotNameSchema = z
  .string({ error: LOT_NAME_REQUIRED_MESSAGE })
  .trim()
  .min(1, LOT_NAME_REQUIRED_MESSAGE);

/** BL-2131's lot-money rule for the fee — the one the create routes parse with. */
const LoadOutFeeWireSchema = lotMoneyWireSchema('loadOutFee').nullable();

/**
 * The load-out fee's issues under BL-2131's lot-money rule, or none.
 *
 * @param fee - The form's fee value.
 * @returns The rule's issues (paths relative to the fee).
 */
function loadOutFeeIssues(fee: unknown): z.core.$ZodIssue[] {
  const result = LoadOutFeeWireSchema.safeParse(fee);
  return result.success ? [] : result.error.issues;
}

/**
 * BL-2080 (ADR 016 — Form Validation § 8, clause 2): drop what the form hides.
 *
 * - **Land fields on a non-Land item.** The Land section is not rendered, so its
 *   acreage is cleared and the pricing type reset to `total` — the values the
 *   form's category-change effect writes and the payload builders already send.
 * - **An unticked load-out fee.** The amount input is hidden, so an invalid
 *   amount is dropped to `null` rather than stored. A valid one is kept as
 *   entered (BUG-1808's shape); the server zeroes the stored amount when the
 *   toggle is off (BUG-2496, `buildLotPatch`), so it never reaches the row.
 *
 * @param data - The validated form values.
 * @returns The values with every hidden field stripped.
 * @sideEffect none — pure.
 */
function stripHiddenItemFields<T extends { categoryId: string; acreage: number | null; pricingType: 'per_acre' | 'total'; hasLoadOutFee: boolean; loadOutFee: MoneyWire | null }>(
  data: T,
): T {
  const land = isLandCategory(data.categoryId) ? {} : { acreage: null, pricingType: 'total' as const };
  const fee = data.hasLoadOutFee || loadOutFeeIssues(data.loadOutFee).length === 0 ? {} : { loadOutFee: null };
  return { ...data, ...land, ...fee };
}

/** The client form's fields, before its cross-field rules (see {@link makeItemFormSchema}). */
const ItemFormShape = z
  .object({
    name: RequiredTitleSchema,
    description: z.string(),
    categoryId: z.string(),
    make: z.string(),
    model: z.string(),
    year: z.string(),
    vin: z.string(),
    serialNumber: z.string(),
    hoursMileage: z.number().nullable(),
    mileageUnit: z.enum(['miles', 'km']),
    // BL-2131: the three prices are MoneyWire in the record tenant's currency
    // (BUG-1882), through the same field the routes parse — so the form refuses
    // a negative, too-large or finer-than-the-currency amount with the routes'
    // own copy before the round trip. `null` = left blank / "no reserve".
    startPrice: lotMoneyWireSchema('startPrice').nullable(),
    reserve: lotMoneyWireSchema('reserve').nullable(),
    sellerId: z.string(),
    sellerNumber: z.string(),
    // BL-1991 — the picked seller's identity record (ADR 058 — Customer
    // Identity), which the seller picker's closed field names both halves from.
    // A display companion of `sellerId` like the two below: no rule, never
    // persisted, so `.optional()` for the same reason they are.
    sellerIdentity: z.custom<CustomerIdentityDto>().nullable().optional(),
    // BL-1449 — the picked seller's own sales rep. Display companions of
    // `sellerId` (like `sellerIdentity`/`sellerNumber`), never edited directly and
    // never persisted; they drive the Sales Rep control's inherited-default
    // label.
    //
    // Declared here so zodResolver doesn't STRIP them (it drops keys the schema
    // doesn't name), but `.optional()` — unlike the submitted lot fields below,
    // which are required to stay 1:1 with `ItemFormData`. These two carry no rule
    // and are never persisted, so requiring them would only make every non-form
    // caller of this schema fail on two fields it has no reason to supply.
    sellerSalesRepId: z.string().nullable().optional(),
    sellerSalesRepName: z.string().nullable().optional(),
    // BL-2748: the lot's contact slots. Optional with no default so the
    // required-field probe never marks them required and an untouched form
    // sends nothing (which leaves the columns alone).
    ...LotContactSlotFields,
    auctionId: z.string(),
    auctionName: z.string(),
    isTitledItem: z.boolean(),
    titleStatus: z.string(),
    // BL-968: the title's US state (inventory_items.title_state). Editable in the
    // auction lot editor's titling section alongside titleStatus; permissive like
    // the rest of the form (the lot PUT owns real validation).
    titleState: z.string(),
    // BUG-1091: title mailing — the post-sale paperwork trail. Lot-held since
    // BL-939/BL-950; permissive here like the rest of the form (the lot PATCH's
    // LotCatalogPatchSchema owns the real enum + width validation).
    titleMailingStatus: z.string(),
    titleMailingTrackingNumber: z.string(),
    titleMailingDateSent: z.string(),
    titleMailingRecipientName: z.string(),
    /** Single stored string — `street\ncity, ST zip`. See TitleMailingFields. */
    titleMailingRecipientAddress: z.string(),
    acreage: z.number().nullable(),
    pricingType: z.enum(['per_acre', 'total']),
    // BL-1767: the lot's tax treatment (auction editor only; the inventory form
    // keeps the Tax Exempt default). AC 4's rule rides the superRefine below.
    taxStatus: z.string(),
    taxRateId: z.string().nullable(),
    // Auction-context lot fields (BL-968) — held by ItemForm's RHF only in the
    // auction EDIT surface, and permissive like the rest of the form (the lot
    // PUT route owns the real validation). Present in the schema so zodResolver
    // keeps them in the submit payload (unknown keys are stripped). Required (not
    // optional) to stay 1:1 with the `ItemFormData` interface — ItemForm's
    // `getInitialFormData` always seeds them, so the form data always carries
    // them, and ItemForm is the sole consumer of this schema via zodResolver.
    // (BL-1053 removed `status` — lot status is no longer a form field.)
    salesRepId: z.string(),
    locationId: z.string(),
    hasLoadOutFee: z.boolean(),
    // BL-2080: typed here, judged in the superRefine below — the amount input is
    // hidden while the fee is unticked, so its rule is conditional on the tick
    // (ADR 016 — Form Validation § 8, clause 2).
    loadOutFee: z.custom<MoneyWire | null>(),
    // BL-2694: whether this lot shows on its seller's "My Consignments" account page.
    visibleToSeller: z.boolean(),
  });

/**
 * The client form's cross-field rules, shared by every {@link makeItemFormSchema} build.
 * @param data - The parsed form values.
 * @param ctx - The Zod refinement context.
 */
function addItemFormIssues(
  data: z.output<typeof ItemFormShape>,
  ctx: z.RefinementCtx,
  phoneRule: LotContactRuleOptions,
): void {
  addSellerRequiredIssue(data, ctx);
  // BUG-2132: a typed Additional contact phone must be a real number.
  addAdditionalContactPhoneIssue(data, ctx, phoneRule);
  // BL-2783: a shown other contact needs a phone or an email (the PATCH runs the same rule).
  addOtherContactReachableIssue(data, ctx, phoneRule);
  // BL-1064: the land per-acre acreage rule — shared verbatim with
  // LotCreatePayloadSchema below, so the form blocks the submit before the
  // round trip and the two sides cannot drift.
  addAcreageRequiredForPerAcreIssue(data, ctx);
  // BL-1904: no tax-pair rule here — a taxable status may save with no rate
  // (picked later), and the server's validateLotTaxSelection agrees.
  // BL-2591: a Land item hides the fee too, so its rule waits for a non-Land
  // category as well as the tick (the submit sends the stored fee on Land).
  if (data.hasLoadOutFee && !isLandCategory(data.categoryId)) {
    for (const issue of loadOutFeeIssues(data.loadOutFee)) {
      ctx.addIssue({ code: 'custom', path: ['loadOutFee', ...issue.path], message: issue.message });
    }
  }
}

/**
 * Client form schema — the shape `ItemForm`'s React Hook Form holds (aligns
 * 1:1 with the exported `ItemFormData` interface). `title`, `seller`, the land
 * per-acre acreage rule and the three prices' lot-money rules (BL-2131) constrain
 * the operator; every other field is typed but permissive. All of them are
 * applied server-side too, so the form rejects exactly what the routes reject —
 * no more, no less.
 *
 * BL-1515 removed `inventoryNumber`: it was a client-only field with no server
 * counterpart, held solely so the create form could render an editable input and
 * duplicate-check it. The number is minted by `allocateNextInventoryNumberTx`
 * inside the insert transaction (ADR 028 §3), so no web form declares it.
 *
 * BUG-2132: the Additional contact carries the phone rule, with the lot's loaded
 * phone grandfathered (advisory — the PATCH re-reads the stored one).
 *
 * @param options - The loaded Additional contact phone (grandfathered when untouched).
 * @returns The form schema.
 */
export function makeItemFormSchema(options: LotContactRuleOptions = {}) {
  return ItemFormShape.superRefine((data, ctx) => addItemFormIssues(data, ctx, options)).transform(stripHiddenItemFields);
}

/** The client form schema with no loaded phone (a new item, or a non-form reader). */
export const ItemFormSchema = makeItemFormSchema();

export type ItemFormInput = z.infer<typeof ItemFormSchema>;

/**
 * The ONE create-request payload shape, shared by both doors (BL-1449).
 *
 * `POST /api/inventory` and the `newLot` branch of `POST /api/lots/place-in-auction`
 * accept the same fields; they differ only in which endpoint receives them and what
 * the server does afterwards. Declaring it once here — beside the two Zod schemas
 * that gate it — replaced a client-side `CreateLotBody` and a near-identical
 * `CreateInventoryItemInput` on the inventory hook, which is how the inventory door
 * came to silently accept a NARROWER field set than the auction door.
 *
 * Deliberately a MINIMAL passthrough of what the operator entered — every default is
 * the SERVER's (ADR 026). No `ringId`, no `status`, no `increment`, no
 * `inventoryNumber`: the routes own all of those. Since BL-1515 no client FORM
 * declares `inventoryNumber` either — the field is gone from `ItemFormSchema`,
 * not merely omitted here.
 *
 * This is a plain interface, not `z.infer` of the schemas below. The place-in-auction
 * schema is a permissive `.passthrough()` GATE (it validates required-ness and a few
 * field rules, not the whole shape), so inferring from it would type the payload as
 * "name plus anything" (BUG-2508: the inventory schema is `.strict()` and declares
 * every key, but its fields are mostly `z.unknown()`, so the interface stays the
 * typed source) — see their own docs for why they are shaped that way.
 */
export interface LotCreateRequestPayload {
  name: string;
  description?: string;
  categoryId?: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  serialNumber?: string;
  hoursMileage?: number | null;
  mileageUnit?: string;
  /** BL-2131: MoneyWire in the tenant's currency; null/absent = left blank (stores 0, BUG-1284). */
  startPrice?: MoneyWire | null;
  /** BL-2131: MoneyWire; null/absent = "no reserve" (stores NULL, BUG-1573). */
  reserve?: MoneyWire | null;
  sellerId?: string;
  isTitledItem?: boolean;
  titleStatus?: string;
  acreage?: number | null;
  pricingType?: string | null;
  /** BL-1767: the lot's Tax Status + picked rate (auction door only). */
  taxStatus?: string;
  taxRateId?: string | null;
  // BL-1449 — the five field groups the Add door used to gate off. Optional at
  // create (RAC 2) but carried identically by both doors (AC 3).
  titleState?: string;
  /** `''` = live-inherit the seller's rep; a staff-user UUID = explicit override. */
  salesRepId?: string;
  locationId?: string;
  hasLoadOutFee?: boolean;
  /** BL-2131: MoneyWire; null = no load-out fee. */
  loadOutFee?: MoneyWire | null;
  /**
   * BL-2694: absent on every UI-driven create (the toggle isn't rendered at
   * create — the server stamps the tenant's default when this key is absent).
   * Present only for a caller stating an explicit value (a seed, a relist).
   */
  visibleToSeller?: boolean;
  /** Absent — not `undefined` — when the tenant has no custom fields (AC 4). */
  customFields?: Record<string, string>;
  /**
   * BL-1451 — external documents attached on the Add form, before this lot
   * existed. Each ref names a `<tenant>/drafts/<session>/…` object the create
   * copies into the new lot's canonical folder (BL-1436).
   *
   * Absent — not `[]` — when nothing was attached, so the overwhelmingly common
   * create ships the payload it always did. Both create schemas already gate the
   * field; this is the hand-written payload interface catching up so the client
   * can set it without a cast.
   */
  stagedDocuments?: StagedDocumentRef[];
  /**
   * BL-1450 — photos staged on the Add form before this record existed. The
   * media twin of `stagedDocuments` above, and the same contract: each ref names
   * a `<tenant>/drafts/<sessionId>/…` object the create copies into the new
   * record's canonical folder inside the same transaction (ADR 026 Rule 1), and
   * the key is absent — not `[]` — on the common photo-less create, so that path
   * never enters the staged-attach flow at all.
   *
   * The two arrays travel together but are not interchangeable: documents land
   * in `auction-documents` keyed on the LOT, photos in `inventory-media` keyed
   * on the INVENTORY ITEM.
   */
  stagedMedia?: StagedMediaRef[];
  /**
   * BL-1452 — title notes typed on the Add form before this record existed.
   * Plain text, not files: the server inserts them against the item it mints,
   * in the create transaction, and stamps the author from the session. Absent
   * (not `[]`) on a create that carries none.
   */
  internalNotes?: string[];
  /**
   * BUG-2635 — the Contacts section's slots. Absent unless the operator changed the section from the house
   * defaults it opened on; the server then stamps the defaults itself. Sent as a full set when present.
   */
  showSalesRep?: boolean;
  showSeller?: boolean;
  showAdditionalContact?: boolean;
  additionalContactTitle?: string | null;
  additionalContactName?: string | null;
  additionalContactPhone?: string | null;
  additionalContactEmail?: string | null;
}

/**
 * Server payload schema for `POST /api/inventory`. Replaces the route's
 * hand-rolled `if (!title)` / year-range / mileage / VIN checks — same rules,
 * now sourced from Zod so a missing required field rides the shared
 * `fieldErrors` envelope (AC 4). `title` is the shared required rule (the same
 * `title` the client form enforces).
 *
 * NOTE — seller IS required here as of BL-1433, and the rule is the same
 * `addSellerRequiredIssue` the client form applies. It used to be deliberately
 * absent on the grounds that sellerless items were a tested server capability;
 * that stopped being true when `auction_lots.seller_id` went NOT NULL, and the
 * asymmetry it created is what BUG-1357 surfaced. This route mints a lot as well
 * as an item, so accepting a sellerless payload here would hand the column a row
 * it refuses. `tenantId` is validated by
 * the route's own `!tenantId` + auth-membership check, not re-validated here.
 * BUG-2508: the object is `.strict()`. Every key the route maps downstream is
 * declared below (the route reads most off the original body, so validation is
 * a gate, not a transform), and a key outside that set is a 400 naming it
 * instead of a 201 over a value the door never stored.
 */
export const InventoryCreatePayloadSchema = z
  .object({
    // Shared with the client form and the UPDATE path — see RequiredTitleSchema.
    name: RequiredTitleSchema,
    // Preserved from the old hand-rolled checks: year in 1900–2100, mileage
    // non-negative, VIN exactly 17 chars when present (empty = not supplied).
    year: z.number().min(1900, INVENTORY_YEAR_MESSAGE).max(2100, INVENTORY_YEAR_MESSAGE).nullable().optional(),
    hoursMileage: z.number().min(0, INVENTORY_HOURS_MILEAGE_MESSAGE).nullable().optional(),
    vin: z.union([z.literal(''), z.string().length(17, INVENTORY_VIN_MESSAGE)]).optional(),
    // BL-1433. `z.unknown().optional()` rather than a typed string: the refine
    // does its own narrowing via `normalizeSellerId`, so typing it here would
    // newly 400 on non-string values this route currently tolerates — a
    // behaviour change beyond the seller rule's scope. Same contract
    // `LotCreatePayloadSchema` documents for its acreage inputs.
    sellerId: z.unknown().optional(),
    // BL-1449 — the five catalog field groups the Add form gained. Declared
    // EXPLICITLY: an unadded key used to be silently accepted and silently
    // dropped, which is exactly how these fields would have looked "wired" while
    // never reaching the row. Typed loosely (`z.unknown()`) for the same reason
    // the acreage inputs are — the real rules live in `validateLotCatalogFields`,
    // which every write door shares, and typing them here would newly 400 on
    // values this route currently tolerates.
    titleState: z.unknown().optional(),
    salesRepId: z.unknown().optional(),
    locationId: z.unknown().optional(),
    hasLoadOutFee: z.unknown().optional(),
    // BL-2694: absent on every UI-driven create; a seed may state it. BUG-2508:
    // a boolean, so `"false"` is a 400 on this field instead of reading as "not
    // true" or being dropped; `toInventoryCreateInput` forwards it to the row.
    visibleToSeller: z.boolean().optional(),
    customFields: z.unknown().optional(),
    // BUG-2508: the rest of the keys the door reads off the body, declared here
    // now that the schema is `.strict()`. Loose on purpose (`z.unknown()`): their
    // rules live in `validateLotCatalogFields` and the service, and typing them
    // here would newly 400 on values the route tolerates today. `tenantId` is
    // proved by the route gate, not re-validated here.
    tenantId: z.unknown().optional(),
    description: z.unknown().optional(),
    categoryId: z.unknown().optional(),
    make: z.unknown().optional(),
    model: z.unknown().optional(),
    serialNumber: z.unknown().optional(),
    mileageUnit: z.unknown().optional(),
    isTitledItem: z.unknown().optional(),
    titleStatus: z.unknown().optional(),
    acreage: z.unknown().optional(),
    pricingType: z.unknown().optional(),
    taxStatus: z.unknown().optional(),
    taxRateId: z.unknown().optional(),
    // BL-2131: the three prices are MoneyWire, validated and decoded once by the
    // shared lot-money field (sign, ceiling, the currency's minor unit; a bare
    // number is malformed). Whether the currency is the one the insert stamps
    // (the tenant's) is the door's check — the schema can't see the tenant.
    startPrice: lotMoneyWireField('startPrice').nullable().optional(),
    reserve: lotMoneyWireField('reserve').nullable().optional(),
    loadOutFee: lotMoneyWireField('loadOutFee').nullable().optional(),
    // BL-1436 staged uploads. Declared EXPLICITLY: an undeclared key used to
    // arrive validated by nothing, and the inventory route's explicit field
    // allowlist then dropped it — the create would succeed while silently
    // discarding a photo the operator just uploaded.
    stagedMedia: StagedMediaRefArraySchema.optional(),
    stagedDocuments: StagedDocumentRefArraySchema.optional(),
    // BL-1452 / BL-1529 — internal notes buffered on the Add form. Declared
    // EXPLICITLY for the same reason as the staged refs above: `.passthrough()`
    // would accept the key, the route's field allowlist would drop it, and the
    // create would 201 having silently discarded the notes the operator just
    // typed. Blank notes are rejected rather than trimmed away — the composer
    // cannot produce one, so a blank arriving here means a caller built the
    // payload by hand.
    internalNotes: InternalNoteBodyArraySchema.optional(),
    // BL-2337 — the offline create's op id. A retry carrying the same id
    // answers with the lot it already made. Declared for the same reason as the
    // fields above: `.passthrough()` would accept it unvalidated.
    clientOpId: OfflineOpIdSchema.optional(),
    // BUG-2635 — the lot's contact slots, from the Add form's Contacts section. The same fields the edit door
    // takes (ADR 016 — Form Validation: one schema), optional so an untouched form sends none and the insert
    // stamps the house defaults.
    ...LotContactSlotFields,
  })
  // BUG-2508: closed, not `.passthrough()`. The door maps the body field by field, so a key it does not
  // read was dropped while the route answered success. `parseBody` names the key (`Unrecognized field "x".`).
  .strict()
  .superRefine((data, ctx) => {
    addSellerRequiredIssue(data as { sellerId?: string | null }, ctx);
    // BUG-2635: no stored contact exists on a create, so the rules judge the values as given.
    addAdditionalContactPhoneIssue(data, ctx);
    addOtherContactReachableIssue(data, ctx);
  });

export type InventoryCreatePayloadInput = z.infer<typeof InventoryCreatePayloadSchema>;

/**
 * Server payload schema for the `newLot` branch of
 * `POST /api/lots/place-in-auction` (reached via `LotPlaceInAuctionSchema` in
 * `./lot-place-in-auction`). Requires `name` — the shared required rule that
 * rides the `fieldErrors` envelope on a missing value (AC 4).
 *
 * BL-1433 corrected this doc: it named `POST /api/auctions/[id]/lots`, which
 * BL-1037 Phase 4a retired (BL-1833 later deleted that route file outright).
 *
 * NOTE — seller is required for this route, but the rule deliberately does NOT
 * live in this schema, and that is not the old client-only asymmetry returning.
 * A payload schema sees only the request body; the lot door's seller may instead
 * be INHERITED from the item's prior lot (BL-1338 — a relist that omits
 * `sellerId` keeps its consignor). A schema-level required rule runs at the
 * route's parse gate, before that inheritance is known, so it would reject
 * exactly the relist BL-1338 exists to support.
 *
 * The invariant is enforced one layer down, in `resolveSellerId`
 * (`lot-create-prepare.ts`), at the only point where the RESOLVED seller is
 * known — as a per-item blocker keyed to `sellerId`, so the operator gets the
 * same inline 400 either way. Compare `InventoryCreatePayloadSchema` above,
 * which HAS the rule inline because that route mints a fresh item and has no
 * inheritance path to wait for.
 *
 * Lot-number / tenant / currency checks stay in the service — this schema covers
 * required-ness and the money fields' shape (BL-2131). `.passthrough()` keeps
 * the lot's other fields the route reads downstream.
 *
 * NOTE — the acreage rule (BL-1064) is applied here too. It replaces a
 * hand-rolled route `if`, so the server keeps its authority; what changes is
 * that the message now rides `fieldErrors.acreage` instead of a bare top-level
 * `error` string.
 */
export const LotCreatePayloadSchema = z
  .object({
    // See RequiredLotNameSchema.
    name: RequiredLotNameSchema,
    // BL-1064 acreage-rule inputs. Deliberately `z.unknown().optional()` — the
    // "trust `!== undefined`, let the check narrow the type" contract. Typing
    // them concretely (e.g. `pricingType` as an enum) would newly 400 on values
    // this route currently writes unvalidated, which is a behavior change well beyond this rule's
    // scope. `addAcreageRequiredForPerAcreIssue` does its own narrowing, so the
    // ONLY new rejection this schema introduces is the intended acreage one.
    categoryId: z.unknown().optional(),
    pricingType: z.unknown().optional(),
    acreage: z.unknown().optional(),
    // BL-1449 — the five catalog field groups the Add form gained. Declared
    // EXPLICITLY because this schema is `.passthrough()`: an unadded key is
    // silently accepted and silently dropped, which is exactly how these fields
    // would have looked "wired" while never reaching the row. Typed loosely
    // (`z.unknown()`) for the same reason the acreage inputs are — the real rules
    // live in `validateLotCatalogFields`, which every write door shares, and
    // typing them here would newly 400 on values this route currently tolerates.
    titleState: z.unknown().optional(),
    salesRepId: z.unknown().optional(),
    locationId: z.unknown().optional(),
    hasLoadOutFee: z.unknown().optional(),
    // BL-2694: declared explicitly for the same passthrough reason as the
    // fields above. Absent on every UI-driven create; a seed may state it.
    visibleToSeller: z.unknown().optional(),
    customFields: z.unknown().optional(),
    // BL-2131: the three prices are MoneyWire, validated and decoded once by the
    // shared lot-money field (sign, ceiling, the currency's minor unit; a bare
    // number is malformed). Whether the currency is the one the insert stamps
    // (the tenant's) is the door's check — the schema can't see the tenant.
    startPrice: lotMoneyWireField('startPrice').nullable().optional(),
    reserve: lotMoneyWireField('reserve').nullable().optional(),
    loadOutFee: lotMoneyWireField('loadOutFee').nullable().optional(),
    // BL-1436 staged uploads. Declared EXPLICITLY, not left to `.passthrough()`:
    // an undeclared key arrives validated by nothing, and the inventory route's
    // explicit field allowlist then drops it — the create would succeed while
    // silently discarding a photo the operator just uploaded.
    stagedMedia: StagedMediaRefArraySchema.optional(),
    stagedDocuments: StagedDocumentRefArraySchema.optional(),
    // BL-1452 / BL-1529 — internal notes buffered on the Add form. Declared
    // EXPLICITLY for the same reason as the staged refs above: `.passthrough()`
    // would accept the key, the route's field allowlist would drop it, and the
    // create would 201 having silently discarded the notes the operator just
    // typed. Blank notes are rejected rather than trimmed away — the composer
    // cannot produce one, so a blank arriving here means a caller built the
    // payload by hand.
    internalNotes: InternalNoteBodyArraySchema.optional(),
    // BL-2337 — the offline create's op id. A retry carrying the same id
    // answers with the lot it already made. Declared for the same reason as the
    // fields above: `.passthrough()` would accept it unvalidated.
    clientOpId: OfflineOpIdSchema.optional(),
    // BUG-2635 — the Add form's Contacts section on the auction door, declared EXPLICITLY for the same
    // passthrough reason as the fields above: an undeclared key is accepted and dropped.
    ...LotContactSlotFields,
  })
  .passthrough()
  .superRefine((data, ctx) => {
    addAcreageRequiredForPerAcreIssue(data, ctx);
    addAdditionalContactPhoneIssue(data, ctx);
    addOtherContactReachableIssue(data, ctx);
  });

export type LotCreatePayloadInput = z.infer<typeof LotCreatePayloadSchema>;
